import {Bell} from "lucide-react";
import {useLayoutEffect, useRef} from "react";

import {Badge} from "@/components/ui/badge";
import {Button} from "@/components/ui/button";
import {cn} from "@/lib/utils";
import {
  COMPLIANCE_STICKY_BAR_REVIEW_BUTTON_CLASS,
  COMPLIANCE_STICKY_BAR_SHELL_CLASS,
  mountComplianceStickyBarHeightSync,
} from "@/src/modules/core/lib/compliance-sticky-bar-layout";
import type {BlockingGateDescriptor} from "@/src/modules/core/lib/compliance-gate-priority";
import {useEscalateCompliance} from "@/src/modules/core/store/compliance-busy";

type ComplianceStickyBarProps = {
  queue: BlockingGateDescriptor[];
};

export function ComplianceStickyBar({queue}: ComplianceStickyBarProps) {
  const escalateNow = useEscalateCompliance();
  const barRef = useRef<HTMLDivElement>(null);
  const top = queue[0];
  const extra = queue.length - 1;

  useLayoutEffect(() => {
    const node = barRef.current;
    if (!node) {
      return;
    }
    return mountComplianceStickyBarHeightSync(node);
  }, []);

  if (!top) {
    return null;
  }

  return (
    <div
      ref={barRef}
      role="status"
      aria-live="polite"
      className={cn(
        "fixed inset-x-0 bottom-0 z-[60] isolate",
        COMPLIANCE_STICKY_BAR_SHELL_CLASS,
        "motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-2 motion-safe:duration-200",
        "px-4 pt-3 pb-[max(0.75rem,var(--app-safe-area-bottom,env(safe-area-inset-bottom,0px)))]",
      )}
    >
      <div className="mx-auto flex max-w-lg items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <span
            className="flex size-10 shrink-0 items-center justify-center rounded-full bg-amber-200 ring-2 ring-amber-400/50 dark:bg-amber-900 dark:ring-amber-500/40"
            aria-hidden
          >
            <Bell className="size-4 text-amber-900 dark:text-amber-100" />
          </span>
          <div className="min-w-0 flex flex-col gap-0.5">
            <div className="flex min-w-0 flex-wrap items-center gap-1.5">
              <p className="truncate text-sm font-semibold text-amber-950 dark:text-amber-50">
                {top.label}
              </p>
              {extra > 0 ? (
                <Badge variant="warning" className="text-xs">
                  +{extra} more
                </Badge>
              ) : null}
            </div>
            <p className="truncate text-xs text-amber-800 dark:text-amber-300">
              Action needed when you finish
            </p>
          </div>
        </div>
        <Button
          size="sm"
          variant="outline"
          className={cn("shrink-0", COMPLIANCE_STICKY_BAR_REVIEW_BUTTON_CLASS)}
          onClick={escalateNow}
        >
          Review now
        </Button>
      </div>
    </div>
  );
}
